import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  Container,
  Button,
  IconButton,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

// Mock data as fallback
const mockReceiptData = {
  id: 1,
  soPhieu: "Điền vào",
  ngay: new Date(2024, 5, 4),
  khachHang: "Tên store",
  items: [],
};

const PhieuNhapNl = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState('user');
  const [userInfo, setUserInfo] = useState({});
  const [isEditMode, setIsEditMode] = useState(false); // New state to track edit mode
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [trangThaii, setTrangThai] = useState("");
  const [newProduct, setNewProduct] = useState({
    maVt: "",
    ten: "",
    soLuong: "",
    slThucNhan: "",
    dvt: "cái",
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isConfirming, setIsConfirming] = useState(false);
  // Add state for button confirmations
  const [bqdConfirmed, setBqdConfirmed] = useState(false);
  const [bqdTimestamp, setBqdTimestamp] = useState(null);
  const [receiverConfirmed, setReceiverConfirmed] = useState(false);
  const [receiverTimestamp, setReceiverTimestamp] = useState(null);
  const [storeConfirmed, setStoreConfirmed] = useState(false);
  const [storeTimestamp, setStoreTimestamp] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [stores, setStores] = useState([]);
  const [storeName, setStoreName] = useState("");
  
  // Helper: find store display name by MaCH (works with various API shapes)
  const getStoreNameByMaCH = (maCH, storesList) => {
    try {
      // Prefer provided list (from fetch), fallback to component state
      const list = Array.isArray(storesList) ? storesList : Array.isArray(stores) ? stores : [];
      if (!maCH && !list.length) {
        return localStorage.getItem("TenCuaHang") || "";
      }

      // Normalize maCH to string for comparisons
      const target = maCH !== null && maCH !== undefined ? String(maCH).trim() : "";

      if (target === "") {
        return localStorage.getItem("TenCuaHang") || "";
      }

      const nameKeys = ["TenCuaHang", "Ten_CH", "TenCH", "Ten", "name", "ten", "Ten_CuaHang"];
      const idKeys = ["MaCH", "Ma_CuaHang", "MaCH_", "IDCH", "id", "Id", "MaCuaHang", "Ma_CH"];

      // Try to find a matching store by various possible id fields
      const found = list.find((s) => {
        if (!s || typeof s !== "object") return false;

        // Try id-like fields
        for (const k of idKeys) {
          if (k in s && s[k] !== null && s[k] !== undefined) {
            if (String(s[k]).trim() === target) return true;
          }
        }

        // Some APIs might expose numeric id while maCH is numeric string
        for (const k of idKeys) {
          if (k in s && !isNaN(s[k]) && !isNaN(target)) {
            if (Number(s[k]) === Number(target)) return true;
          }
        }

        return false;
      });

      if (found) {
        for (const k of nameKeys) {
          if (k in found && found[k]) return String(found[k]);
        }
        // Last resort: return one of common fields
        if (found.Ten) return String(found.Ten);
        if (found.name) return String(found.name);
      }

      // If no match, try to lookup in localStorage or return the raw maCH
      return localStorage.getItem("TenCuaHang") || target || "";
    } catch (err) {
      console.error("Error in getStoreNameByMaCH:", err);
      return localStorage.getItem("TenCuaHang") || (maCH ? String(maCH) : "");
    }
  };

  // Get user role from localStorage
  const getUserRole = () => {
    const maPB = localStorage.getItem('MaPB');
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    const userIDCH = localStorage.getItem('IDCH');
    
    const userInfo = {
      maPB: maPB,
      isAdmin: isAdmin,
      idCH: userIDCH
    };
    
    setUserInfo(userInfo);
    
    // Admin permissions
    if (isAdmin && ['4', '145', '170', '187', '250'].includes(maPB)) {
      return 'admin';
    }
    
    // Coordinator permissions
    if (['80', '53', '4'].includes(maPB)) {
      return 'coordinator';
    }
    
    // Regular user
    return 'user';
  };

  useEffect(() => {
    // Check for admin privileges and user role
    try {
      const role = getUserRole();
      setUserRole(role);
      setIsAdmin(role === 'admin');
    } catch (error) {
      console.error("Error checking user role:", error);
      setUserRole('user');
      setIsAdmin(false);
    }

    // Check if we're in edit mode based on URL
    const currentPath = window.location.pathname;
    setIsEditMode(currentPath.includes("/edit"));

    // Initialize receipt with basic info, items will be loaded from API
    setReceipt({
      id: parseInt(id),
      soPhieu: `PN${id}`,
      ngay: new Date(),
      khachHang: localStorage.getItem("TenCuaHang") || "Tên store",
      items: [], // Sẽ được cập nhật từ API
      trangThai: trangThaii,
      receiverConfirmed: true,
      receiverTimestamp: new Date(),
      receiverMessage: "Đã xác nhận gửi phiếu",
    });
  }, [id]);

  useEffect(() => {
    const fetchFixedItems = async () => {
      try {
        const response = await fetch(
          "https://doticom.vn:3000/api/sp-phieu-nhap"
        );
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const data = await response.json();
        console.log("📦 Fixed Items from API:", data);

        const mappedFixedItems = data.map((item) => ({
          maVt: item.MaSP,
          ten: item.Ten_SP,
          soLuong: null, // Will be filled from receipt details
          slThucNhan: null, // Will be filled from receipt details
          dvt: item.DVT_SP,
          giaMua: item.GiaMua,
        }));

        console.log("📦 Mapped Fixed Items:", mappedFixedItems);
        return mappedFixedItems;
      } catch (error) {
        console.error("Error fetching fixed items:", error);
        return [];
      }
    };

    const fetchStores = async () => {
      try {
        const response = await fetch("https://doticom.vn:3000/stores");
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const data = await response.json();
        console.log("🏪 Stores from API:", data);
        setStores(data);
        return data;
      } catch (error) {
        console.error("Error fetching stores:", error);
        return [];
      }
    };

    const fetchReceiptDetails = async (maPN) => {
      try {
        const response = await fetch(
          `https://doticom.vn:3000/api/sale-nhap-hang-detail/${maPN}`
        );
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const data = await response.json();
        console.log("📋 Receipt Details from API:", data);
        
        // Extract MaCH from receipt details
        const maCH = data[0]?.MaCH;
        console.log("🏪 Extracted MaCH from receipt:", maCH);
        
        const items = data[0].Items || [];
        
        // Check if there's a discrepancy between ordered and received quantities
        const hasDiscrepancy = items.some(
          (item) => item.SLNhapDau !== item.SLThucNhan && 
                    item.SLNhapDau && item.SLNhapDau > 0 &&
                    item.SLThucNhan !== null && item.SLThucNhan !== undefined
        );
        
        let trangThai;
        if (data[0].Act_phieu) {
          trangThai = "Đã duyệt";
          setBqdConfirmed(true);
          setBqdTimestamp(new Date(data[0].NgayNhap));
          setReceipt ({
            trangThai: "Đã duyệt",
            ngay: new Date(data[0].NgayNhap),
            thang: new Date(data[0].NgayNhap).getMonth() + 1,
            nam: new Date(data[0].NgayNhap).getFullYear(),
            soPhieu: data[0].Phieu,
            khachHang: data[0].MaCH,
            maCH: maCH, // Store MaCH for later use
            bqdApproved: true,
            bqdTimestamp: new Date(data[0].NgayNhap),
            bqdMessage: "Đã kiểm soát BQD",
          });
        } else if (data[0].Status === 1 && hasDiscrepancy) {
          trangThai = "Đã nhận, có chênh lệch";
        } else if (data[0].Status === 1) {
          trangThai = "Cửa hàng đã xác nhận";
        } else {
          trangThai = "Đã gửi phiếu";
        }
        
        setTrangThai(trangThai);
        
        // Check if store has confirmed - Only items with SoLuong need SLThucNhan
        const itemsWithSoLuong = items.filter(item => 
          item.SLNhapDau && item.SLNhapDau > 0
        );
        
        const hasStoreConfirmed = itemsWithSoLuong.length > 0 && itemsWithSoLuong.every(item => 
          item.SLThucNhan !== null && 
          item.SLThucNhan !== undefined && 
          item.SLThucNhan !== "" && 
          item.SLThucNhan !== 0
        );
        
        console.log("📋 Store confirmation check:", {
          totalItems: items.length,
          itemsWithSoLuong: itemsWithSoLuong.length,
          itemsWithSLThucNhan: itemsWithSoLuong.filter(item => 
            item.SLThucNhan !== null && 
            item.SLThucNhan !== undefined && 
            item.SLThucNhan !== "" && 
            item.SLThucNhan !== 0
          ).length,
          hasStoreConfirmed,
          hasDiscrepancy
        });
        
        if (hasStoreConfirmed) {
          setStoreConfirmed(true);
          setStoreTimestamp(new Date(data[0].NgayNhap));
        }

        // Extract timestamp from IDTimeNhap
        let receiverTimestamp = null;
        if (data[0].IDTimeNhap) {
          // Remove characters from ! onwards (!47)
          const timestampString = data[0].IDTimeNhap.split("!")[0];
          receiverTimestamp = new Date(timestampString);
          console.log("⏰ Extracted receiver timestamp:", receiverTimestamp);
        }

        const mappedReceiptDetails = items.map((item) => ({
          maVt: item.MaSP,
          ten: item.TenSP,
          soLuong: item.SLNhapDau, // Số Lượng = SoLuong (not SLNhapDau)
          slThucNhan: item.SLThucNhan, // SL Thực Nhận = SLThucNhan
          dvt: item.DVT || "cái", // Fallback nếu không có DVT
          giaMua: item.GiaMua,
          // Also store the additional fields for reference
          slNhapDau: item.SLNhapDau, // Store this for reference if needed
          thanhTien: item.ThanhTien,
        }));

        console.log("📋 Mapped Receipt Details:", mappedReceiptDetails);
        return { items: mappedReceiptDetails, receiverTimestamp, maCH, trangThai };
      } catch (error) {
        console.error("Error fetching receipt details:", error);
        return { items: [], receiverTimestamp: null, maCH: null, trangThai: "Đã gửi phiếu" };
      }
    };

    const initializeReceipt = async () => {
      setLoading(true);
      try {
        const [fixedItems, receiptDetailsResult, storesList] = await Promise.all([
          fetchFixedItems(),
          fetchReceiptDetails(id),
          fetchStores(),
        ]);

        const receiptDetails = receiptDetailsResult.items;
        const apiReceiverTimestamp = receiptDetailsResult.receiverTimestamp;
        const maCH = receiptDetailsResult.maCH;
        const statusFromAPI = receiptDetailsResult.trangThai;

        console.log("🔄 Starting merge process...");
        console.log("🔄 Fixed Items count:", fixedItems.length);
        console.log("🔄 Receipt Details count:", receiptDetails.length);
        console.log("⏰ API Receiver Timestamp:", apiReceiverTimestamp);
        console.log("🏪 MaCH for store matching:", maCH);

        // Get the correct store name
        const correctStoreName = getStoreNameByMaCH(maCH, storesList);
        setStoreName(correctStoreName);

        // Merge fixed items with receipt details by matching product codes
        const mergedItems = fixedItems.map((fixedItem) => {
          // Find matching item in receipt details by comparing MaSP
          const detailItem = receiptDetails.find(
            (item) => item.maVt === fixedItem.maVt
          );

          console.log(`🔍 Processing ${fixedItem.maVt}:`, {
            fixedItem,
            detailItem,
            found: !!detailItem,
          });

          if (detailItem) {
            const merged = {
              ...fixedItem,
              soLuong: detailItem.soLuong, 
              slThucNhan: detailItem.slThucNhan, 
              ten: detailItem.ten || fixedItem.ten,
              dvt: detailItem.dvt || fixedItem.dvt, 
              giaMua: detailItem.giaMua || fixedItem.giaMua, 
            };
            console.log(`✅ Merged item for ${fixedItem.maVt}:`, merged);
            return merged;
          } else {
            // If not found in receipt details, keep the fixed item as is
            console.log(
              `❌ No detail found for ${fixedItem.maVt}, keeping as is`
            );
            return fixedItem;
          }
        });

        // Filter out items that don't have soLuong (null or empty)
        const itemsWithQuantities = mergedItems.filter(item => 
          item.soLuong !== null && 
          item.soLuong !== undefined && 
          item.soLuong !== "" && 
          item.soLuong !== 0
        );

        const additionalItems = receiptDetails.filter(
          (detailItem) =>
            !fixedItems.some((fixedItem) => fixedItem.maVt === detailItem.maVt)
        );

        console.log("➕ Additional items not in fixed list:", additionalItems);
        console.log("🔍 Items filtered by quantities:", itemsWithQuantities.length, "out of", mergedItems.length);

        // Combine filtered items with additional items
        let finalItems = [...itemsWithQuantities, ...additionalItems];

        // Auto-fill slThucNhan with soLuong ONLY if:
        // 1. Item has soLuong 
        // 2. slThucNhan is empty/null
        // 3. AND the receipt status is NOT indicating store confirmation (to preserve API data)
        const shouldPreserveApiData = statusFromAPI === "Đã nhận, có chênh lệch" || statusFromAPI === "Cửa hàng đã xác nhận";
        
        finalItems = finalItems.map(item => {
          // Only auto-fill if item has soLuong and slThucNhan is empty/null
          // BUT don't auto-fill if we should preserve API data (store has confirmed)
          if (item.soLuong && 
              (item.slThucNhan === null || 
               item.slThucNhan === undefined || 
               item.slThucNhan === "") && 
              !shouldPreserveApiData) {
            console.log(`🔄 Auto-filling slThucNhan for ${item.maVt}: ${item.soLuong}`);
            return { ...item, slThucNhan: item.soLuong };
          }
          return item;
        });

        console.log("🎯 FINAL ITEMS FOR DISPLAY:", finalItems);
        console.log("🎯 Final items count:", finalItems.length);

        // Log each final item with its quantities
        finalItems.forEach((item, index) => {
          console.log(`🎯 Final Item ${index + 1}:`, {
            maVt: item.maVt,
            ten: item.ten,
            soLuong: item.soLuong,
            slThucNhan: item.slThucNhan,
            dvt: item.dvt,
          });
        });

        setReceipt((prev) => ({
          ...prev,
          items: finalItems,
          khachHang: correctStoreName, // Use the matched store name
          maCH: maCH, // Store MaCH for reference
          trangThai: statusFromAPI, // Use status from API
          receiverConfirmed: !!apiReceiverTimestamp,
          receiverTimestamp: apiReceiverTimestamp,
          // Ensure BQD states are preserved if already set
          bqdApproved: prev?.bqdApproved || false,
          bqdTimestamp: prev?.bqdTimestamp || null,
          bqdMessage: prev?.bqdMessage || null,
        }));

        // Update the confirmation states
        if (apiReceiverTimestamp) {
          setReceiverConfirmed(true);
          setReceiverTimestamp(apiReceiverTimestamp);
        }
      } catch (error) {
        console.error("Error initializing receipt:", error);
        // Fallback to empty items array if API fails
        setReceipt((prev) => ({
          ...prev,
          items: [],
          khachHang: localStorage.getItem("TenCuaHang") || "Tên store",
        }));
      } finally {
        setLoading(false);
      }
    };

    initializeReceipt();
  }, [id]);

  useEffect(() => {
    // Initialize confirmation states from receipt data
    if (receipt) {
      setBqdConfirmed(receipt.bqdApproved || false);
      setBqdTimestamp(receipt.bqdTimestamp || null);
      setReceiverConfirmed(receipt.receiverConfirmed || false);
      setReceiverTimestamp(receipt.receiverTimestamp || null);
    }
  }, [receipt]);

   const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const handleGoBack = () => {
    navigate("/nhaphangnl");
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEdit = () => {
    navigate(`/new-phieu-nhap-nguyen-lieu/${id}/edit`);
  };

  // Update status based on BQD approval and discrepancies
  const getReceiptStatus = () => {
    if (receipt?.trangThai === "Đã duyệt" || receipt?.bqdApproved || bqdConfirmed) {
      return "Đã duyệt";
    }
    if (receipt?.trangThai === "Đã nhận, có chênh lệch") {
      return "Đã nhận, có chênh lệch";
    }
    if (receipt?.trangThai === "Cửa hàng đã xác nhận") {
      return "Cửa hàng đã xác nhận";
    }
    return "Đã gửi phiếu";
  };

  const confirmAction = async (button, message, isBqdAction = false) => {
    if (button.classList.contains("confirmed")) {
      return;
    }

    // Nếu là hành động BQD (xác nhận kiểm soát), gọi API
    if (isBqdAction) {
      setIsConfirming(true);

      try {
        // Chuẩn bị dữ liệu cho API
        const chiTietSanPham = receipt.items
          .filter(
            (item) =>
              item.maVt && item.slThucNhan !== null && item.slThucNhan !== ""
          )
          .map((item) => ({
            maSP: item.maVt,
            slThucNhan: parseFloat(item.slThucNhan) || 0, // Changed from parseInt to parseFloat
          }));

        if (chiTietSanPham.length === 0) {
          setSnackbarMessage(
            "Vui lòng nhập số lượng thực nhận cho ít nhất một sản phẩm"
          );
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
          setIsConfirming(false);
          return;
        }

        const requestBody = {
          maPhieuNhap: id,
          chiTietSanPham: chiTietSanPham,
        };

        console.log("📤 Sending API request:", requestBody);

        const response = await fetch(
          "https://doticom.vn:3000/api/xac-nhan-kiem-soat",
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(responseData.message || "Failed to confirm control");
        }

        console.log("✅ API Response:", responseData);

        const now = new Date();

        // Update states
        setBqdConfirmed(true);
        setBqdTimestamp(now);

        // Cập nhật UI sau khi API thành công
        button.textContent = "✓ Đã xác nhận";
        button.classList.add("confirmed");

        const statusDiv = button.nextElementSibling;
        statusDiv.textContent = message;
        statusDiv.style.color = "#2196F3";
        statusDiv.style.fontWeight = "bold";

        // Add timestamp
        const timestamp = now.toLocaleString("vi-VN");
        setTimeout(() => {
          statusDiv.textContent = message + "\n" + timestamp;
        }, 500);

        // Update receipt state
        setReceipt((prev) => ({
          ...prev,
          bqdApproved: true,
          bqdTimestamp: now,
          bqdMessage: message,
          trangThai: "Đã duyệt",
          lastUpdated: now,
          updateCount: (prev.updateCount || 0) + 1,
        }));

        // Update localStorage
        try {
          const storedReceipts =
            JSON.parse(localStorage.getItem("phieuNhapList")) || [];
          const updatedReceipts = storedReceipts.map((r) => {
            if (r.id === parseInt(id)) {
              return {
                ...r,
                bqdApproved: true,
                bqdTimestamp: now,
                bqdMessage: message,
                trangThai: "Đã duyệt",
                lastUpdated: now,
                updateCount: (r.updateCount || 0) + 1,
              };
            }
            return r;
          });
          localStorage.setItem(
            "phieuNhapList",
            JSON.stringify(updatedReceipts)
          );
        } catch (error) {
          console.error("Error updating localStorage:", error);
        }

        // Show success message
        setSnackbarMessage(
          `Xác nhận kiểm soát thành công! Đã cập nhật ${responseData.data.soSanPhamCapNhat} sản phẩm.`
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Error confirming control:", error);
        setSnackbarMessage(`Lỗi khi xác nhận kiểm soát: ${error.message}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setIsConfirming(false);
      }
    } else {
      // Xử lý hành động không phải BQD (như cũ)
      button.textContent = "✓ Đã xác nhận";
      button.classList.add("confirmed");

      const statusDiv = button.nextElementSibling;
      statusDiv.textContent = message;
      statusDiv.style.color = "#2196F3";
      statusDiv.style.fontWeight = "bold";

      const now = new Date();
      const timestamp = now.toLocaleString("vi-VN");
      setTimeout(() => {
        statusDiv.textContent = message + "\n" + timestamp;
      }, 500);

      // Update local state for non-BQD actions
      setReceipt((prev) => ({
        ...prev,
        receiverConfirmed: true,
        receiverTimestamp: now,
        receiverMessage: message,
        lastUpdated: now,
        updateCount: (prev.updateCount || 0) + 1,
      }));

      // Update localStorage for non-BQD actions
      try {
        const storedReceipts =
          JSON.parse(localStorage.getItem("phieuNhapList")) || [];
        const updatedReceipts = storedReceipts.map((r) => {
          if (r.id === parseInt(id)) {
            return {
              ...r,
              receiverConfirmed: true,
              receiverTimestamp: now,
              receiverMessage: message,
              lastUpdated: now,
              updateCount: (r.updateCount || 0) + 1,
            };
          }
          return r;
        });
        localStorage.setItem("phieuNhapList", JSON.stringify(updatedReceipts));
      } catch (error) {
        console.error("Error updating localStorage:", error);
      }

      setSnackbarMessage("Đã xác nhận gửi phiếu");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }
  };


    const handleBqdConfirmation = async () => {
    if (bqdConfirmed || isConfirming) return;

    setIsConfirming(true);

    try {
      // Prepare API data
      const chiTietSanPham = receipt.items
        .filter(
          (item) =>
            item.maVt && item.slThucNhan !== null && item.slThucNhan !== ""
        )
        .map((item) => ({
          maSP: item.maVt,
          slThucNhan: parseFloat(item.slThucNhan) || 0, // Changed from parseInt to parseFloat
        }));

      if (chiTietSanPham.length === 0) {
        setSnackbarMessage(
          "Vui lòng nhập số lượng thực nhận cho ít nhất một sản phẩm"
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        setIsConfirming(false);
        return;
      }

      const requestBody = {
        maPhieuNhap: id,
        chiTietSanPham: chiTietSanPham,
      };

      const response = await fetch(
        "https://doticom.vn:3000/api/xac-nhan-kiem-soat",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || "Failed to confirm control");
      }

      const now = new Date();
      
      // Update states
      setBqdConfirmed(true);
      setBqdTimestamp(now);

      // Update receipt state
      setReceipt((prev) => ({
        ...prev,
        bqdApproved: true,
        bqdTimestamp: now,
        bqdMessage: "Đã kiểm soát BQD",
        trangThai: "Đã duyệt",
        lastUpdated: now,
        updateCount: (prev.updateCount || 0) + 1,
      }));

      // Update localStorage
      try {
        const storedReceipts =
          JSON.parse(localStorage.getItem("phieuNhapList")) || [];
        const updatedReceipts = storedReceipts.map((r) => {
          if (r.id === parseInt(id)) {
            return {
              ...r,
              bqdApproved: true,
              bqdTimestamp: now,
              bqdMessage: "Đã kiểm soát BQD",
              trangThai: "Đã duyệt",
              lastUpdated: now,
              updateCount: (r.updateCount || 0) + 1,
            };
          }
          return r;
        });
        localStorage.setItem("phieuNhapList", JSON.stringify(updatedReceipts));
      } catch (error) {
        console.error("Error updating localStorage:", error);
      }

      setSnackbarMessage(
        `Xác nhận kiểm soát thành công! Đã cập nhật ${responseData.data.soSanPhamCapNhat} sản phẩm.`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error confirming control:", error);
      setSnackbarMessage(`Lỗi khi xác nhận kiểm soát: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleReceiverConfirmation = () => {
    if (receiverConfirmed) return;

    const now = new Date();
    setReceiverConfirmed(true);
    setReceiverTimestamp(now);

    // Update receipt state
    setReceipt((prev) => ({
      ...prev,
      receiverConfirmed: true,
      receiverTimestamp: now,
      receiverMessage: "Đã xác nhận gửi phiếu",
      lastUpdated: now,
      updateCount: (prev.updateCount || 0) + 1,
    }));

    // Update localStorage
    try {
      const storedReceipts =
        JSON.parse(localStorage.getItem("phieuNhapList")) || [];
      const updatedReceipts = storedReceipts.map((r) => {
        if (r.id === parseInt(id)) {
          return {
            ...r,
            receiverConfirmed: true,
            receiverTimestamp: now,
            receiverMessage: "Đã xác nhận gửi phiếu",
            lastUpdated: now,
            updateCount: (r.updateCount || 0) + 1,
          };
        }
        return r;
      });
      localStorage.setItem("phieuNhapList", JSON.stringify(updatedReceipts));
    } catch (error) {
      console.error("Error updating localStorage:", error);
    }

    setSnackbarMessage("Đã xác nhận gửi phiếu");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  // Handle opening the add product dialog
  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    // Reset the form
    setNewProduct({
      maVt: "",
      ten: "",
      soLuong: "",
      slThucNhan: "",
      dvt: "cái",
    });
    setSelectedCategory("");
    setSelectedProduct("");
    setFormErrors({});
    
    // Fetch categories when opening dialog
    if (categories.length === 0) {
      fetchCategories();
    }
  };

  // Handle closing the add product dialog
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  // Handle input changes in the add product form
  const handleNewProductChange = (e) => {
    const { name, value } = e.target;
    setNewProduct((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Add function to fetch categories
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = await fetch("https://doticom.vn:3000/categories");
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      
      const transformedCategories = data.map((cat) => ({
        id: cat.MaNLSP,
        name: cat.TenLoaiSP,
      }));
      
      setCategories(transformedCategories);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setSnackbarMessage("Không thể tải danh mục sản phẩm");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Add function to fetch products by category
  const fetchProductsByCategory = async (categoryId) => {
    if (!categoryId) return;
    
    setLoadingProducts(true);
    try {
      const response = await fetch(
        `https://doticom.vn:3000/items?category=${categoryId}`
      );
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      
      const transformedProducts = data.map((product) => ({
        id: product.MaSP,
        name: product.Ten_SP,
        price: parseFloat(product.DonGia) || 0,
        price2: parseFloat(product.DonGia2) || 0,
        unit: product.DVT_SP || "",
      }));
      
      setProducts(prev => ({
        ...prev,
        [categoryId]: transformedProducts
      }));
    } catch (error) {
      console.error("Error fetching products:", error);
      setSnackbarMessage("Không thể tải sản phẩm");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Handle category selection
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    setSelectedProduct("");
    
    // Reset product info
    setNewProduct(prev => ({
      ...prev,
      maVt: "",
      ten: "",
    }));
    
    // Fetch products for selected category
    if (categoryId && !products[categoryId]) {
      fetchProductsByCategory(categoryId);
    }
  };

  // Handle product selection
  const handleProductChange = (e) => {
    const productId = e.target.value;
    setSelectedProduct(productId);
    
    if (productId && selectedCategory) {
      const selectedProductData = products[selectedCategory]?.find(p => p.id === productId);
      if (selectedProductData) {
        setNewProduct(prev => ({
          ...prev,
          maVt: selectedProductData.id,
          ten: selectedProductData.name,
          dvt: selectedProductData.unit || "cái",
        }));
      }
    }
  };

  // Validate the form before submission
  const validateForm = () => {
    const errors = {};

    if (!selectedCategory) {
      errors.category = "Vui lòng chọn danh mục";
    }

    if (!selectedProduct) {
      errors.product = "Vui lòng chọn sản phẩm";
    }

    if (!newProduct.soLuong || parseFloat(newProduct.soLuong) <= 0) { // Changed from parseInt to parseFloat
      errors.soLuong = "Vui lòng nhập số lượng hợp lệ";
    }

    // For coordinators, only validate soLuong
    // For other users, slThucNhan is optional (will be auto-filled if empty)
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle adding the new product to the receipt
  const handleAddNewProduct = () => {
    if (!validateForm()) {
      return;
    }

    // Check if the product code already exists
    const existingProduct = receipt.items.find(
      (item) => item.maVt === newProduct.maVt
    );
    if (existingProduct) {
      setSnackbarMessage("Sản phẩm với mã này đã tồn tại trong phiếu.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    // Auto-fill slThucNhan with soLuong if slThucNhan is empty
    const finalSlThucNhan = newProduct.slThucNhan || newProduct.soLuong;

    // Add the new product to the receipt
    const updatedItems = [
      ...receipt.items,
      {
        maVt: newProduct.maVt,
        ten: newProduct.ten,
        soLuong: newProduct.soLuong || null,
        slThucNhan: finalSlThucNhan || null,
        dvt: newProduct.dvt,
        isCustom: true, // Mark as a custom-added product
      },
    ];

    setReceipt({
      ...receipt,
      items: updatedItems,
    });

    // Show success message
    setSnackbarMessage("Đã thêm sản phẩm mới vào phiếu.");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);

    // Close the dialog
    handleCloseAddDialog();
  };

  const updateReceipt = async () => {
    try {
      const requestBody = {
        Items: receipt.items.map((item) => ({
          MaSP: item.maVt,
          TenSP: item.ten,
          SoLuong: parseFloat(item.slThucNhan) || 0, // Changed from parseInt to parseFloat
          GiaMua: item.giaMua || 0,
          GiamPT: item.giamPT || 0,
          ThanhTien: (parseFloat(item.slThucNhan) || 0) * (item.giaMua || 0), // Changed from parseInt to parseFloat
        })),
      };

      const response = await fetch(
        `https://doticom.vn:3000/api/sale-nhap-hang/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update receipt");
      }

      setSnackbarMessage("Cập nhật phiếu nhập thành công!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error updating receipt:", error);
      setSnackbarMessage("Có lỗi xảy ra khi cập nhật phiếu nhập!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };
  

  // Add new function for store confirmation
  const handleStoreConfirmation = async () => {
    if (storeConfirmed || isConfirming) return;

    setIsConfirming(true);

    try {
      // Check if ALL product types with SoLuong have SLThucNhan values before proceeding
      const productTypesWithSoLuongButMissingSLThucNhan = receipt.items.filter(item => 
        item.maVt && 
        item.soLuong && 
        item.soLuong > 0 && 
        (
          item.slThucNhan === null || 
          item.slThucNhan === undefined || 
          item.slThucNhan === ""
        )
      );

      if (productTypesWithSoLuongButMissingSLThucNhan.length > 0) {
        setSnackbarMessage(
          `Vui lòng nhập số lượng thực nhận cho tất cả loại bánh có số lượng. Còn thiếu ${productTypesWithSoLuongButMissingSLThucNhan.length} loại bánh chưa có số lượng thực nhận.`
        );
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        setIsConfirming(false);
        return;
      }

      // Prepare API data - only include items that have SoLuong and SLThucNhan
      const chiTietSanPham = receipt.items
        .filter(item => 
          item.maVt && 
          item.soLuong && 
          item.soLuong > 0 &&
          item.slThucNhan !== null && 
          item.slThucNhan !== undefined && 
          item.slThucNhan !== ""
        )
        .map((item) => ({
          maSP: item.maVt,
          soLuong: parseFloat(item.slThucNhan) || 0, // Changed from parseInt to parseFloat - Set SoLuong = SLThucNhan
          slThucNhan: parseFloat(item.slThucNhan) || 0, // Changed from parseInt to parseFloat
        }));

      const requestBody = {
        maPhieuNhap: id,
        chiTietSanPham: chiTietSanPham,
      };

      const response = await fetch(
        "https://doticom.vn:3000/api/xac-nhan-nhan-hang",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || "Failed to confirm receipt");
      }

      const now = new Date();
      
      // Update states
      setStoreConfirmed(true);
      setStoreTimestamp(now);

      // Update receipt state
      setReceipt((prev) => ({
        ...prev,
        storeConfirmed: true,
        storeTimestamp: now,
        storeMessage: "Đã xác nhận nhận hàng",
        lastUpdated: now,
        updateCount: (prev.updateCount || 0) + 1,
      }));

      setSnackbarMessage(
        `Xác nhận nhận hàng thành công! Đã cập nhật ${responseData.data?.soSanPhamCapNhat || chiTietSanPham.length} loại bánh.`
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error confirming receipt:", error);
      setSnackbarMessage(`Lỗi khi xác nhận nhận hàng: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsConfirming(false);
    }
  };

  // Check if user can edit SL Thực Nhận
  const canEditSlThucNhan = () => {
    return userRole === 'user' && !storeConfirmed && !bqdConfirmed;
  };

  // Check if add product button should be shown
  const shouldShowAddProduct = () => {
    return userRole !== 'coordinator' && !isEditMode;
  };

  // Check if edit button should be shown
  const shouldShowEditButton = () => {
    return userRole === 'coordinator' || userRole === 'admin';
  };

  // Auto-fill SL Thực nhận with Số Lượng for empty cells
  const handleAutoFillSlThucNhan = () => {
    const updatedItems = receipt.items.map(item => {
      // Only fill if SL Thực nhận is empty/null and Số Lượng has a value
      if (
        item.soLuong && 
        item.soLuong !== "" && 
        item.soLuong !== 0 &&
        (
          item.slThucNhan === null || 
          item.slThucNhan === undefined || 
          item.slThucNhan === "" || 
          item.slThucNhan === 0
        )
      ) {
        return { ...item, slThucNhan: item.soLuong };
      }
      return item;
    });

    setReceipt({ ...receipt, items: updatedItems });

    // Show success message
    const filledCount = updatedItems.filter((item, index) => 
      item.slThucNhan !== receipt.items[index].slThucNhan
    ).length;

    if (filledCount > 0) {
      setSnackbarMessage(`Đã tự động điền ${filledCount} sản phẩm.`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } else {
      setSnackbarMessage("Không có sản phẩm nào cần điền.");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
    }
  };

  // Helper function to check if SL Thực Nhận differs from Số Lượng
  const isSlThucNhanDiff = (item) => {
    // Only highlight if both values are numbers and not empty/null
    if (
      item &&
      item.soLuong !== undefined &&
      item.soLuong !== null &&
      item.soLuong !== "" &&
      item.slThucNhan !== undefined &&
      item.slThucNhan !== null &&
      item.slThucNhan !== ""
    ) {
      // Compare as numbers
      return Number(item.soLuong) !== Number(item.slThucNhan);
    }
    return false;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Typography>Đang tải phiếu nhập...</Typography>
      </Container>
    );
  }

  if (!receipt) {
    return (
      <Container maxWidth="lg">
        <Typography>Không tìm thấy phiếu nhập</Typography>
      </Container>
    );
  }

  // Group items into columns for table layout (vertical first, then horizontal)
  const organizeItemsIntoColumns = () => {
    if (!receipt || !receipt.items) {
      console.log("⚠️ No receipt or items found for organizing columns");
      return [];
    }

    console.log("📊 Organizing items into columns:", receipt.items);

    // Check if we're on mobile (screen width <= 768px)
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // On mobile, use single column layout (4 columns: Mã VT, Tên, Số lượng, SL thực nhận)
      return [receipt.items];
    }

    // Number of columns we want to display on desktop
    const numColumns = 3;

    // Calculate how many items will be in each column
    const itemsPerColumn = Math.ceil(receipt.items.length / numColumns);

    // Create the columns structure
    const columns = [];
    for (let col = 0; col < numColumns; col++) {
      columns.push([]);
    }

    // Fill columns with items (vertical first, then horizontal)
    receipt.items.forEach((item, index) => {
      // Calculate which column this item belongs to
      const columnIndex = Math.floor(index / itemsPerColumn);

      // If we've exceeded our columns, don't add it
      if (columnIndex < numColumns) {
        columns[columnIndex].push(item);
      }
    });

    // Make sure all columns have the same number of rows by adding empty items
    // This ensures our table layout doesn't break
    for (let col = 0; col < columns.length; col++) {
      while (columns[col].length < itemsPerColumn) {
        columns[col].push({ maVt: "", ten: "", soLuong: "", slThucNhan: null });
      }
    }

    console.log("📊 Organized columns:", columns);
    return columns;
  };

  // Replace the existing chunkItems variable with organizedColumns
  const organizedColumns = organizeItemsIntoColumns();
  const isMobile = window.innerWidth <= 768;

  // Format date for display
  const day = receipt?.ngay instanceof Date ? receipt.ngay.getDate() : "--";
  const month =
    receipt?.ngay instanceof Date ? receipt.ngay.getMonth() + 1 : "--";
  const year =
    receipt?.ngay instanceof Date ? receipt.ngay.getFullYear() : "----";

  // Format update timestamp for display
  const formatUpdateHistory = () => {
    if (!receipt || !receipt.lastUpdated) return null;

    const updateDate = new Date(receipt.lastUpdated);
    const formattedDate = updateDate.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    const updateCount = receipt.updateCount || 1;

    return `Cập nhật lần cuối: ${formattedDate} (Số lần cập nhật: ${updateCount})`;
  };

  return (
    <Box>
      {/* Header actions - only visible when not printing */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          "@media print": { display: "none" },
          "@media (max-width: 440px)": {
            p: 1,
            gap: 1,
          },
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          variant="outlined"
          sx={{
            "@media (max-width: 440px)": {
              minWidth: "60px",
              padding: "4px 8px",
              fontSize: "10px",
              "& .MuiButton-startIcon": {
                marginRight: "2px",
                "& > *:nth-of-type(1)": {
                  fontSize: "14px",
                },
              },
            },
          }}
        >
          <span style={{ display: window.innerWidth <= 440 ? 'none' : 'inline' }}>
            Quay lại
          </span>
        </Button>
        <Box sx={{
          display: "flex",
          gap: 1,
          "@media (max-width: 440px)": {
            gap: 0.5,
          },
        }}>
          {shouldShowEditButton() && (
            <Button
              startIcon={<EditIcon />}
              onClick={handleEdit}
              variant="outlined"
              sx={{ 
                mr: 1,
                "@media (max-width: 440px)": {
                  mr: 0,
                  minWidth: "60px",
                  padding: "4px 8px",
                  fontSize: "10px",
                  "& .MuiButton-startIcon": {
                    marginRight: "2px",
                    "& > *:nth-of-type(1)": {
                      fontSize: "14px",
                    },
                  },
                },
              }}
            >
              <span style={{ display: window.innerWidth <= 440 ? 'none' : 'inline' }}>
                Chỉnh sửa
              </span>
            </Button>
          )}
          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            variant="contained"
            color="primary"
            sx={{
              "@media (max-width: 440px)": {
                minWidth: "60px",
                padding: "4px 8px",
                fontSize: "10px",
                "& .MuiButton-startIcon": {
                  marginRight: "2px",
                  "& > *:nth-of-type(1)": {
                    fontSize: "14px",
                  },
                },
              },
            }}
          >
            <span style={{ display: window.innerWidth <= 440 ? 'none' : 'inline' }}>
              In phiếu
            </span>
          </Button>
        </Box>
      </Box>

      {/* Receipt layout based on the provided HTML */}
      <div className="receipt">
        <div className="header">
          <div className="logo">
            DOTI
            <br />
            COM
          </div>
          <div className="title">PHIẾU GIAO HÀNG</div>
          <div className="subtitle">20 20 30 90 96</div>
        </div>

        <div className="info-section">
          <div className="info-left">
            <div>
              Khách hàng / Bakery:{" "}
              <span className="handwritten">{storeName || receipt.khachHang}</span>
            </div>
            <div>
              Ngày: <span className="handwritten">{day}</span> tháng{" "}
              <span className="handwritten">{month}</span> năm{" "}
              <span className="handwritten">{year}</span>
            </div>
          </div>
          <div className="info-right">
            <div>Ký hiệu: DT/BN</div>
            <div>
              Số: {receipt.soPhieu}
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="items-table">
            <thead>
              <tr>
                {isMobile ? (
                  // Mobile layout: Single set of 4 columns
                  <>
                    <th className="code-col">Mã VT</th>
                    <th className="name-col">Tên Mặt Hàng</th>
                    <th className="qty-col">Số Lượng</th>
                    <th className="qty-col">SL Thực Nhận</th>
                  </>
                ) : (
                  // Desktop layout: Multiple column sets
                  organizedColumns.map((_, colIndex) => (
                    <React.Fragment key={`header-${colIndex}`}>
                      <th className="code-col">Mã vt</th>
                      <th className="name-col">Tên Mặt Hàng</th>
                      <th className="qty-col">Số Lượng</th>
                      <th className="qty-col">SL Thực Nhận</th>
                    </React.Fragment>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {isMobile ? (
                // Mobile layout: Simple rows
                organizedColumns[0]?.map((item, rowIndex) => (
                  <tr key={`mobile-row-${rowIndex}`}>
                    <td>{item.maVt}</td>
                    <td>{item.ten}</td>
                    <td className="handwritten">
                      <input
                        type="text"
                        value={item.soLuong || ""}
                        readOnly={true}
                        title="Số lượng"
                        style={
                          isSlThucNhanDiff(item)
                            ? { backgroundColor: "#fff3cd" }
                            : undefined
                        }
                      />
                    </td>
                    <td className="handwritten">
                      <input
                        type="text"
                        value={item.slThucNhan || ""}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          const newItems = [...receipt.items];
                          if (newItems[rowIndex]) {
                            newItems[rowIndex] = {
                              ...newItems[rowIndex],
                              slThucNhan: newValue,
                            };
                            setReceipt({ ...receipt, items: newItems });
                          }
                        }}
                        title="Số lượng thực nhận"
                        disabled={!canEditSlThucNhan()}
                        style={
                          isSlThucNhanDiff(item)
                            ? { backgroundColor: "#fff3cd" }
                            : undefined
                        }
                        inputMode="decimal" // Added to support decimal input on mobile
                        pattern="[0-9]*\.?[0-9]*" // Added pattern for decimal numbers
                      />
                    </td>
                  </tr>
                ))
              ) : (
                // Desktop layout: Complex multi-column rows
                Array.from({ length: organizedColumns[0]?.length || 0 }).map(
                  (_, rowIndex) => (
                    <tr key={`row-${rowIndex}`}>
                      {organizedColumns.map((column, colIndex) => {
                        const item = column[rowIndex];
                        if (!item) return null;

                        return (
                          <React.Fragment key={`item-${rowIndex}-${colIndex}`}>
                            <td>{item.maVt}</td>
                            <td>{item.ten}</td>
                            <td className="handwritten">
                              <input
                                type="text"
                                value={item.soLuong || ""}
                                readOnly={true}
                                title="Số lượng"
                                style={
                                  isSlThucNhanDiff(item)
                                    ? { backgroundColor: "#fff3cd" }
                                    : undefined
                                }
                              />
                            </td>
                            <td className="handwritten">
                              <input
                                type="text"
                                value={item.slThucNhan || ""}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  const newItems = [...receipt.items];
                                  const originalIndex =
                                    colIndex * (organizedColumns[0]?.length || 0) +
                                    rowIndex;
                                  if (newItems[originalIndex]) {
                                    newItems[originalIndex] = {
                                      ...newItems[originalIndex],
                                      slThucNhan: newValue,
                                    };
                                    setReceipt({ ...receipt, items: newItems });
                                  }
                                }}
                                title="Số lượng thực nhận"
                                disabled={!canEditSlThucNhan()}
                                style={
                                  isSlThucNhanDiff(item)
                                    ? { backgroundColor: "#fff3cd" }
                                    : undefined
                                }
                                inputMode="decimal" // Added to support decimal input on mobile
                                pattern="[0-9]*\.?[0-9]*" // Added pattern for decimal numbers
                              />
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>

          {/* Auto-fill button - only visible when user can edit and there are items to fill */}
          {canEditSlThucNhan() && receipt?.items?.some(item => 
            item.soLuong && 
            item.soLuong !== "" && 
            item.soLuong !== 0 &&
            (
              item.slThucNhan === null || 
              item.slThucNhan === undefined || 
              item.slThucNhan === "" || 
              item.slThucNhan === 0
            )
          ) && (
            <Box
              sx={{
                mt: 2,
                textAlign: "center",
                "@media print": { display: "none" },
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleAutoFillSlThucNhan}
                size="small"
                sx={{ mr: 1 }}
              >
                Tự động điền SL Thực nhận
              </Button>
            </Box>
          )}

          {/* Add custom product button - only visible when not printing and based on permissions */}
          {shouldShowAddProduct() && !storeConfirmed && (
            <Box
              sx={{
                mt: 2,
                textAlign: "center",
                "@media print": { display: "none" },
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
                size="small"
              >
                Thêm sản phẩm mới
              </Button>
            </Box>
          )}
        </div>

        <div className="total-section">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <strong>Trạng thái: </strong>
              <span
                className={`status-badge ${getReceiptStatus()
                  .toLowerCase()
                  .replace(/\s/g, "-")}`}
              >
                {getReceiptStatus()}
              </span>
            </div>
            <div>
              <strong>
                <span className="handwritten">RDB - Rổ Đựng bánh</span>
              </strong>
            </div>
          </div>

          {!isEditMode && formatUpdateHistory() && (
            <div className="update-history">{formatUpdateHistory()}</div>
          )}
        </div>

        <div className="signature-section">
          {/* Store confirmation button - only for users */}
          {userRole === 'user' && (
            <div className="signature-box">
              <div className="signature-title">
                Cửa hàng xác nhận nhận hàng
                <br />
                (Ký, ghi rõ họ tên)
              </div>
              {!storeConfirmed ? (
                <button
                  className="confirm-btn"
                  onClick={handleStoreConfirmation}
                  disabled={isConfirming}
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {isConfirming && (
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  )}
                  {isConfirming ? "Đang xử lý..." : "Xác nhận nhận hàng"}
                </button>
              ) : (
                <div className="status-text">
                  <div className="confirmed-text">✓ Đã xác nhận nhận hàng</div>
                  Đã nhận hàng vào ngày
                  <br />
                  {storeTimestamp && 
                    new Date(storeTimestamp).toLocaleString("vi-VN")
                  }
                </div>
              )}
            </div>
          )}

          {/* Coordinator signature box - only for coordinators or if already confirmed */}
          {(userRole === 'coordinator' || (userRole !== 'coordinator' && receiverConfirmed)) && (
            <div className="signature-box">
              <div className="signature-title">
                Điều phối
                <br />
                (Ký, ghi rõ họ tên)
              </div>
              {userRole === 'coordinator' ? (
                <>
                  {isEditMode ? (
                    <button
                      className="confirm-btn confirmed"
                      onClick={updateReceipt}
                    >
                      Cập nhật phiếu gửi
                    </button>
                  ) : (
                    <>
                      {!receiverConfirmed ? (
                        <button
                          className="confirm-btn"
                          onClick={handleReceiverConfirmation}
                        >
                          Xác nhận gửi phiếu
                        </button>
                      ) : (
                        <div className="status-text">
                          <div className="confirmed-text">✓ Đã gửi phiếu</div>
                          Đã gửi phiếu vào ngày
                          <br />
                          {receiverTimestamp && 
                            new Date(receiverTimestamp).toLocaleString("vi-VN")
                          }
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="status-text">
                  <div className="confirmed-text">✓ Đã gửi phiếu</div>
                  Đã gửi phiếu vào ngày
                  <br />
                  {receiverTimestamp && 
                    new Date(receiverTimestamp).toLocaleString("vi-VN")
                  }
                </div>
              )}
            </div>
          )}

          {/* BQD signature box - only for admins or if already confirmed */}
          {(userRole === 'admin' || (userRole !== 'admin' && bqdConfirmed)) && (
            <div className="signature-box">
              <div className="signature-title">
                Kiểm soát BQD
                <br />
                (Ký, ghi rõ họ tên)
              </div>
              {userRole === 'admin' ? (
                <>
                  <button
                    className={`confirm-btn ${bqdConfirmed ? "confirmed" : ""}`}
                    onClick={handleBqdConfirmation}
                    disabled={bqdConfirmed || isConfirming}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    {isConfirming && (
                      <CircularProgress size={16} sx={{ color: "white" }} />
                    )}
                    {bqdConfirmed
                      ? "✓ Đã xác nhận"
                      : isConfirming
                      ? "Đang xử lý..."
                      : "Xác nhận kiểm soát"}
                  </button>
                  {bqdConfirmed && bqdTimestamp && (
                    <div className="status-text">
                      Đã kiểm soát BQD
                      <br />
                      {new Date(bqdTimestamp).toLocaleString("vi-VN")}
                    </div>
                  )}
                </>
              ) : (
                <div className="status-text">
                  <div className="confirmed-text">✓ Đã kiểm soát BQD</div>
                  Đã kiểm soát BQD vào ngày
                  <br />
                  {bqdTimestamp && 
                    new Date(bqdTimestamp).toLocaleString("vi-VN")
                  }
                </div>
              )}
            </div>
          )}

          {/* Show confirmed status for store confirmation if user is not 'user' but store has confirmed */}
          {userRole !== 'user' && storeConfirmed && (
            <div className="signature-box">
              <div className="signature-title">
                Cửa hàng xác nhận nhận hàng
                <br />
                (Ký, ghi rõ họ tên)
              </div>
              <div className="status-text">
                <div className="confirmed-text">✓ Đã xác nhận nhận hàng</div>
                Đã nhận hàng vào ngày
                <br />
                {storeTimestamp && 
                  new Date(storeTimestamp).toLocaleString("vi-VN")
                }
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog for adding a new product */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Thêm sản phẩm mới</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Category Selection */}
            <FormControl 
              fullWidth 
              margin="normal" 
              size="small"
              error={!!formErrors.category}
            >
              <InputLabel>Danh mục sản phẩm</InputLabel>
              <Select
                value={selectedCategory}
                label="Danh mục sản phẩm"
                onChange={handleCategoryChange}
                disabled={loadingCategories}
              >
                <MenuItem value="">
                  <em>-- Chọn danh mục --</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.category}
                </Typography>
              )}
            </FormControl>

            {/* Product Selection */}
            <FormControl 
              fullWidth 
              margin="normal" 
              size="small"
              error={!!formErrors.product}
              disabled={!selectedCategory}
            >
              <InputLabel>Sản phẩm</InputLabel>
              <Select
                value={selectedProduct}
                label="Sản phẩm"
                onChange={handleProductChange}
                disabled={!selectedCategory || loadingProducts}
              >
                <MenuItem value="">
                  <em>-- Chọn sản phẩm --</em>
                </MenuItem>
                {selectedCategory && products[selectedCategory]?.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.name} {product.unit && `(${product.unit})`}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.product && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {formErrors.product}
                </Typography>
              )}
            </FormControl>

            {/* Product Info Display */}
            {selectedProduct && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Mã sản phẩm:</strong> {newProduct.maVt}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Tên sản phẩm:</strong> {newProduct.ten}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Đơn vị tính:</strong> {newProduct.dvt}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label="Số lượng"
                name="soLuong"
                value={newProduct.soLuong}
                onChange={handleNewProductChange}
                error={!!formErrors.soLuong}
                helperText={formErrors.soLuong}
                type="number"
                size="small"
                sx={{ width: userRole === 'coordinator' ? "100%" : "50%" }}
                inputProps={{ min: 0, step: "0.1" }} // Changed min to 0 and added step for decimals
              />

              {/* Show SL Thực nhận for non-coordinators but make it optional */}
              {userRole !== 'coordinator' && (
                <TextField
                  label="SL Thực nhận (tùy chọn)"
                  name="slThucNhan"
                  value={newProduct.slThucNhan}
                  onChange={handleNewProductChange}
                  error={!!formErrors.slThucNhan}
                  helperText={formErrors.slThucNhan || "Để trống sẽ tự động điền bằng Số lượng"}
                  type="number"
                  size="small"
                  sx={{ width: "50%" }}
                  inputProps={{ min: 0, step: "0.1" }} // Changed min to 0 and added step for decimals
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} color="inherit">
            Hủy bỏ
          </Button>
          <Button
            onClick={handleAddNewProduct}
            variant="contained"
            color="primary"
            disabled={!selectedProduct}
          >
            Thêm sản phẩm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: Arial, sans-serif;
          background-color: #f5f5f5;
          padding: 20px;
        }

        .receipt {
          max-width: 1200px;
          margin: 0 auto;
          background: white;
          padding: 20px;
          border: 2px solid #333;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
          width: 100%;
        }

        .header {
          text-align: center;
          margin-bottom: 20px;
          position: relative;
        }

        .logo {
          position: absolute;
          left: 0;
          top: 0;
          width: 60px;
          height: 60px;
          background: linear-gradient(45deg, #ff6b35, #f7931e);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
        }

        .title {
          color: #d32f2f;
          font-size: 28px;
          font-weight: bold;
          margin-top: 10px;
          letter-spacing: 2px;
        }

        .subtitle {
          font-style: italic;
          color: #666;
          margin-top: 5px;
        }

        .info-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
        }

        .info-left {
          flex: 1;
        }

        .info-right {
          text-align: right;
        }

        .table-container {
          overflow-x: auto;
          margin-bottom: 30px;
          width: 100%;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
          table-layout: fixed;
          min-width: 800px;
        }

        .items-table th,
        .items-table td {
          border: 1px solid #333;
          padding: 4px 6px;
          text-align: left;
          word-wrap: break-word;
        }

        .items-table th {
          background-color: #ffcccb;
          font-weight: bold;
          text-align: center;
        }

        .code-col {
          width: 60px;
        }

        .name-col {
          width: 140px;
        }

        .qty-col {
          width: 50px;
          text-align: center;
        }

        .handwritten {
          font-family: "Courier New", monospace;
          color: #0066cc;
          font-weight: bold;
        }

        .handwritten input {
          width: 100%;
          border: none;
          background: transparent;
          border-bottom: 1px dashed #999;
          color: #0066cc;
          font-weight: bold;
          text-align: center;
          padding: 2px;
          font-family: "Courier New", monospace;
          font-size: 12px;
          /* Highlight if SL Thực Nhận differs from Số Lượng */
          /* The inline style above will override this if needed */
        }

        .signature-section {
          display: flex;
          justify-content: space-around;
          gap: 20px;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 2px solid #333;
        }

        .signature-box {
          text-align: center;
          padding: 20px 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          background-color: #f9f9f9;
          flex: 1;
        }

        .signature-title {
          font-weight: bold;
          margin-bottom: 15px;
          color: #333;
          font-size: 13px;
        }

        .confirm-btn {
          background: linear-gradient(45deg, #4caf50, #45a049);
          color: white;
          border: none;
          padding: 12px 20px;
          border-radius: 25px;
          cursor: pointer;
          font-size: 14px;
          font-weight: bold;
          transition: all 0.3s ease;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          width: 100%;
          min-height: 48px;
        }

        .confirm-btn:hover {
          background: linear-gradient(45deg, #45a049, #4caf50);
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
        }

        .confirm-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .confirm-btn.confirmed {
          background: linear-gradient(45deg, #2196f3, #1976d2);
          cursor: default;
        }

        .confirm-btn.confirmed:hover {
          transform: none;
          background: linear-gradient(45deg, #2196f3, #1976d2);
        }

        .status-text {
          font-size: 11px;
          margin-top: 8px;
          color: #666;
          font-style: italic;
        }

        .total-section {
          margin-top: 20px;
          text-align: right;
          padding: 10px;
          background-color: #f0f0f0;
          border-radius: 5px;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
          color: white;
        }

        .status-badge.đã-gửi-phiếu {
          background-color: #ff9800;
        }

        .status-badge.đã-duyệt {
          background-color: #2196f3;
        }

        .status-badge.hoàn-thành {
          background-color: #4caf50;
        }

        .status-badge.đã-nhận-có-chênh-lệch {
          background-color: #FF9800;
        }
        
        .status-badge.cửa-hàng-đã-xác-nhận {
          background-color: #2196F3;
        }

        .update-history {
          font-size: 12px;
          font-style: italic;
          color: #666;
          margin-top: 8px;
          text-align: left;
        }

        .status-text .confirmed-text {
          color: #2196f3;
          font-weight: bold;
          font-size: 14px;
          margin-bottom: 5px;
        }

        .status-text .pending-confirmation {
          color: #ff9800;
          font-weight: bold;
          font-size: 14px;
        }

        /* Mobile responsive styles */
        @media (max-width: 768px) {
          body {
            padding: 10px;
          }

          .receipt {
            padding: 15px;
            max-width: 100%;
            border-width: 1px;
          }

          .header {
            margin-bottom: 15px;
          }

          .logo {
            width: 50px;
            height: 50px;
            font-size: 10px;
          }

          .title {
            font-size: 20px;
            margin-top: 5px;
            letter-spacing: 1px;
          }

          .subtitle {
            font-size: 12px;
          }

          .info-section {
            flex-direction: column;
            gap: 10px;
            margin-bottom: 15px;
          }

          .info-right {
            text-align: left;
          }

          .table-container {
            margin-bottom: 20px;
          }



          .items-table {
            font-size: 10px;
            min-width: 100%;
          }

          .items-table th,
          .items-table td {
            padding: 3px 4px;
          }

          .code-col {
            width: 20%;
          }

          .name-col {
            width: 40%;
          }

          .qty-col {
            width: 20%;
                       text-align: center;
          }

          .handwritten input {
            font-size: 10px;
            padding: 1px;
          }

          .signature-section {
            flex-direction: column;
            gap: 15px;
            margin-top: 20px;
            padding-top: 15px;
          }

          .signature-box {
            padding: 15px 10px;
          }

          .signature-title {
            font-size: 12px;
            margin-bottom: 12px;
          }

          .confirm-btn {
            padding: 10px 15px;
            font-size: 12px;
            min-height: 44px;
          }

          .status-text {
            font-size: 10px;
          }

          .total-section {
            padding: 8px;
            text-align: center;
          }

          .status-badge {
            font-size: 11px;
            padding: 3px 8px;
          }

          .update-history {
            font-size: 10px;
          }
        }

        /* Extra small mobile devices */
        @media (max-width: 480px) {
          body {
            padding: 5px;
          }

          .receipt {
            padding: 10px;
          }

          .title {
            font-size: 18px;
          }

          .items-table {
            font-size: 9px;
            min-width: 100%;
          }

          .items-table th,
          .items-table td {
            padding: 2px 3px;
          }

          .code-col {
            width: 18%;
          }

          .name-col {
            width: 42%;
          }

          .qty-col {
            width: 20%;
          }

          .handwritten input {
            font-size: 9px;
          }

          .signature-box {
            padding: 12px 8px;
          }

          .confirm-btn {
            font-size: 11px;
            padding: 8px 12px;
            min-height: 40px;
          }

          .status-text {
            font-size: 9px;
          }

          .total-section {
            padding: 6px;
            text-align: center;
            font-size: 11px;
          }

          .status-badge {
            font-size: 9px;
            padding: 2px 6px;
          }

          .update-history {
            font-size: 8px;
            text-align: center;
          }

          .handwritten {
            font-size: 8px;
          }

          /* Specific adjustments for very narrow screens */
          .info-section {
            font-size: 10px;
          }

          .info-left,
          .info-right {
            text-align: left;
          }

          /* Make table more compact */
          .items-table th {
            font-size: 7px;
            padding: 1px;
          }

          /* Adjust button container for mobile */
          .signature-section .confirm-btn {
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
        }

        /* Ultra small mobile devices - 400px and below */
        @media (max-width: 400px) {
          body {
            padding: 3px;
            font-size: 12px;
          }

          .receipt {
            padding: 8px;
            border-width: 1px;
          }

          .header {
            margin-bottom: 10px;
          }

          .logo {
            width: 40px;
            height: 40px;
            font-size: 8px;
            position: relative;
            margin: 0 auto 10px;
          }

          .title {
            font-size: 16px;
            margin-top: 0;
            letter-spacing: 0.5px;
            line-height: 1.2;
          }

          .subtitle {
            font-size: 10px;
          }

          .info-section {
            flex-direction: column;
            gap: 8px;
            margin-bottom: 12px;
            font-size: 11px;
          }

          .info-left div,
          .info-right div {
            margin-bottom: 5px;
            border-width: 1px;
          }

          .code-col {
            width: 15%;
          }

          .name-col {
            width: 45%;
          }

          .qty-col {
            width: 20%;
          }

          .handwritten input {
            font-size: 8px;
            padding: 1px;
            min-height: 18px;
          }

          .signature-section {
            flex-direction: column;
            gap: 10px;
            margin-top: 15px;
            padding-top: 10px;
          }

          .signature-box {
            padding: 10px 6px;
            margin-bottom: 8px;
          }

          .signature-title {
            font-size: 10px;
            margin-bottom: 10px;
            line-height: 1.2;
          }

          .confirm-btn {
            font-size: 10px;
            padding: 6px 8px;
            min-height: 36px;
            border-radius: 18px;
            white-space: nowrap;
          }

          .status-text {
            font-size: 8px;
            line-height: 1.2;
          }

          .total-section {
            padding: 6px;
            text-align: center;
            font-size: 11px;
          }

          .status-badge {
            font-size: 9px;
            padding: 2px 6px;
          }

          .update-history {
            font-size: 8px;
            text-align: center;
          }

          .handwritten {
            font-size: 8px;
          }
        }

        /* Ultra narrow screens - 350px and below */
        @media (max-width: 350px) {
          .receipt {
            padding: 5px;
          }

          .title {
            font-size: 14px;
          }

          .items-table {
            min-width: 100%;
            font-size: 7px;
          }

          .code-col {
            width: 15%;
          }

          .name-col {
            width: 45%;
          }

          .qty-col {
            width: 20%;
          }

          .handwritten input {
            font-size: 7px;
            min-height: 16px;
          }

          .confirm-btn {
            font-size: 9px;
            padding: 5px 6px;
            min-height: 32px;
          }

          .signature-title {
            font-size: 9px;
          }

          .status-text {
            font-size: 7px;
          }

          .total-section {
            font-size: 10px;
          }
        }

        /* Landscape orientation on mobile */
        @media (max-width: 768px) and (orientation: landscape) {
        /* Landscape orientation on mobile */
        @media (max-width: 768px) and (orientation: landscape) {
          .signature-section {
            flex-direction: row;
            gap: 10px;
          }

          .signature-box {
            flex: 1;
            padding: 12px 8px;
          }

          .confirm-btn {
            font-size: 11px;
            padding: 8px 10px;
          }
        }

        /* Landscape for ultra small screens */
        @media (max-width: 400px) and (orientation: landscape) {
          .signature-section {
            flex-direction: row;
            gap: 8px;
            flex-direction: row;
            gap: 8px;
          }

          .signature-box {
            padding: 8px 4px;
          }

          .confirm-btn {
            font-size: 9px;
            padding: 4px 6px;
            min-height: 30px;
          }

          .signature-title {
            font-size: 8px;
            margin-bottom: 8px;
          }
        }

        @media print {
          body {
            padding: 0;
          }
          .receipt {
            max-width: 100%;
            border: none;
            box-shadow: none;
          }
          .handwritten input {
            border-bottom: 1px solid #999;
          }
          .items-table {
            font-size: 10px;
          }
        }
      `}</style>
    </Box>
  );
};

export default PhieuNhapNl;
